import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { getCurrentUser } from 'src/app/core/services/functions.service';
import { getEndpoint } from 'src/app/core/services/functions.service';

export interface NovedadComercial {
  id: number;
  periodo: string;
  c_tiponovedad: string;
  c_ven: string;
  dias: number;
  observaciones: string;
  estado: string;
  c_emp: string;
  usuario_creacion: string;
  fecha_creacion?: Date;
  usuario_modificacion?: string;
  fecha_modificacion?: Date;
}

export interface NovedadComercialBulkLoad {
  periodo: string;
  c_vend: string;
  c_tiponovedad: string;
  dias: number;
  observaciones: string;
}

export interface ResultadoCargaNovedad {
  periodo: string;
  c_vend: string;
  c_tiponovedad: string;
  dias: number;
  estado: string;
  mensaje: string;
}

export interface BulkLoadResponse {
  registrosInsertados: number;
  registrosEliminados: number;
  registrosFallidos: ResultadoCargaNovedad[];
  mensaje: string;
}

@Injectable({
  providedIn: 'root'
})
export class NovedadesComercialesService {
  //private apiUrl = 'http://localhost:8000/coms-novedades-comerciales';
  ENDPOINT = getEndpoint('coms_novedades_comerciales');
  private httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };

  constructor(
    private http: HttpClient,
  ) { }

  getNovedades(company: string): Observable<NovedadComercial[]> {
    return this.http.get<NovedadComercial[]>(`${this.ENDPOINT}/`, {
      headers: { 'company': company }
    })
    .pipe(
      catchError(error => {
        throw error;
      })
    );
  }

  getNovedad(id: number): Observable<NovedadComercial> {
    return this.http.get<NovedadComercial>(`${this.ENDPOINT}/${id}/`);
  }

  createNovedad(novedad: Partial<NovedadComercial>): Observable<NovedadComercial> {
    const currentUser = getCurrentUser();
    const novedadCompleta = {
      ...novedad,
      c_emp: currentUser?.c_emp || 'JA',
      usuario_creacion: currentUser?.usuario_aplicativo || 'SYSTEM',
      estado: 'A'
    };
    return this.http.post<NovedadComercial>(`${this.ENDPOINT}/`, novedadCompleta, {
      headers: { 'company': currentUser?.c_emp || 'JA' }
    }).pipe(
      catchError(error => {
        // Si el error tiene un mensaje específico del backend, lo usamos
        if (error.error?.detail) {
          return throwError(() => new Error(error.error.detail));
        }
        // Si no, usamos el mensaje genérico
        return throwError(() => error);
      })
    );
  }

  updateNovedad(id: number, novedad: Partial<NovedadComercial>): Observable<NovedadComercial> {
    const currentUser = getCurrentUser();
    const novedadCompleta = {
      ...novedad,
      c_emp: currentUser?.c_emp || 'JA',
      usuario_modificacion: currentUser?.usuario_aplicativo || 'SYSTEM',
      estado: 'A'
    };
    return this.http.put<NovedadComercial>(`${this.ENDPOINT}/${id}`, novedadCompleta, {
      headers: { 'company': currentUser?.c_emp || 'JA' }
    }).pipe(
      catchError(error => {
        // Si el error tiene un mensaje específico del backend, lo usamos
        if (error.error?.detail) {
          return throwError(() => new Error(error.error.detail));
        }
        // Si no, usamos el mensaje genérico
        return throwError(() => error);
      })
    );
  }

  deleteNovedad(id: number): Observable<void> {
    const currentUser = getCurrentUser();
    return this.http.delete<void>(`${this.ENDPOINT}/${id}/`, {
      headers: { 'company': currentUser?.c_emp || 'JA' }
    });
  }

  procesarArchivoCargaMasiva(formData: FormData): Observable<any> {
    return this.http.post(`${this.ENDPOINT}/carga-masiva/`, formData);
  }

  bulkLoad(company: string, data: any[], replace: boolean = false, periodo?: string): Observable<BulkLoadResponse> {
    const currentUser = getCurrentUser();
    const usuario = currentUser?.usuario_aplicativo;
    const c_emp = currentUser?.c_emp;
    
    if (!usuario) {
      throw new Error('El usuario es requerido');
    }
    if (!c_emp) {
      throw new Error('No se encontró la empresa del usuario');
    }

    const payload = {
      datos : data,
      usuario: usuario,
      c_emp: c_emp
    };
    
    const url = replace && periodo 
      ? `${this.ENDPOINT}/bulk-load-replace/${periodo}`
      : `${this.ENDPOINT}/bulk-load/`;

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'company': company
    });

    return this.http.post<BulkLoadResponse>(url, payload, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        let errorMessage = 'Ocurrió un error al procesar el archivo';
        if (error.error?.detail) {
          errorMessage = error.error.detail;
        } else if (error.error?.message) {
          errorMessage = error.error.message;
        }
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  createBulk(datos: NovedadComercialBulkLoad[]): Observable<any> {
    return this.http.post(`${this.ENDPOINT}/bulk-load/`, { datos }, this.httpOptions);
  }
}
